/*****************************************************
 * Packages
 ******************************************************/
import React, { useState } from 'react';
import { useDrop, useDrag } from 'react-dnd';
import styled, { css } from 'styled-components';

/*****************************************************
 * Locals
 ******************************************************/
import Modal from './Modal';
import ModalHeader from './ModalHeader';

function getSize({ size }) {
    switch (size) {
        case 'md': {
            return css`
                width: 800px;
            `;
        }
        case 'lg': {
            return css`
                width: 930px;
            `;
        }
        case 'sm':
        default:
            return css`
                width: 400px;
            `;
    }
}

const ModalDropzone = styled.div`
    width: 100%;
    height: 100%;
`;

const ModalWrapper = styled.div`
    height: 650px;
    overflow: hidden;
    max-height: 650px;
    position: absolute;
    border-radius: 5px;
    box-shadow: 0 0 60px 0 rgba(43, 53, 86, 0.15);
    background: ${({ theme }) => theme.modalBg};

    ${getSize};
`;

const ModalFooter = styled.div`
    display: flex;
    padding: 15px 30px;
    align-items: center;
    border-radius: 0 0 5px 5px;
    justify-content: space-between;
    background: ${({ theme }) => theme.primary.bg};
    border-top: 2px solid ${({ theme }) => theme.inputBorder};

    .undo-redo {
        padding-left: 8px;
        padding-right: 8px;
    }

    .icon {
        color: ${({ theme }) => theme.primary.fg};
    }
`;

const EditorModalBase = (props) => {
    const {
        title,
        close,
        children,
        style = {},
        size = 'sm',
        tabs = null,
        footer = null,
        hasBackdrop = true,
    } = props;
    const [isDraggings, setDragging] = useState(false);

    const [position, setPosition] = useState({ top: 80, left: 30 });

    const [, drop] = useDrop({
        accept: 'MODAL',
        drop(item, monitor) {
            const delta = monitor.getDifferenceFromInitialOffset();
            const left = Math.round(item.left + delta.x);
            const top = Math.round(item.top + delta.y);
            setPosition({ left, top });
            return undefined;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        begin() {
            setDragging(true);
        },
        end() {
            setDragging(false);
        },
        item: { left: position.left, top: position.top, type: 'MODAL' },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0 : 1;

    return (
        <Modal isDragging={isDraggings} hasBackdrop={hasBackdrop}>
            <ModalDropzone ref={drop}>
                <ModalWrapper
                    size={size}
                    ref={preview}
                    style={{ ...position, opacity }}
                >
                    <ModalHeader ref={drag} size={size} close={close}>
                        {title}
                    </ModalHeader>
                    {tabs}
                    {isDraggings ? null : <div>{children}</div>}
                    {footer && <ModalFooter>{footer}</ModalFooter>}
                </ModalWrapper>
            </ModalDropzone>
        </Modal>
    );
};

export default React.memo(EditorModalBase);
